import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RefreshButton from '../components/RefreshButton'
import ImportButton from '../components/ImportButton'
import ActionBar from '../components/ActionBar'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { thorjavaMenuApi } from '../api/thorjavaMenu'
import { empresasApi } from '../api/empresas'
import { ThorJavaMenuItemResponse, Empresa } from '../types/api'
import { useAuth } from '../contexts/AuthContext'
import { NivelAcesso } from '../types/auth'
import ThorJavaMenuTreeView from '../components/ThorJavaMenu/ThorJavaMenuTreeView'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  )
}

const MenuThorJavaPage: React.FC = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [empresaImportId, setEmpresaImportId] = useState<number | null>(null)
  const [dialogImportOpen, setDialogImportOpen] = useState(false)
  const [substituirExistente, setSubstituirExistente] = useState(false)
  const [tabValue, setTabValue] = useState(0)

  const isSuperAdmin = user?.nivel_acesso === NivelAcesso.SUPER_ADMIN

  // Buscar menu em árvore
  const { data: menuArvore = [], isLoading, error, refetch } = useQuery<ThorJavaMenuItemResponse[]>({
    queryKey: ['thorjava-menu', 'arvore'],
    queryFn: () => thorjavaMenuApi.getArvore({ ativo: true, incluir_separadores: true }),
    enabled: true,
  })

  // Buscar empresas para importação
  const { data: empresas = [] } = useQuery<Empresa[]>({
    queryKey: ['empresas'],
    queryFn: () => empresasApi.list(),
    enabled: isSuperAdmin && dialogImportOpen,
  })

  // Mutation para importar menu
  const importarMutation = useMutation({
    mutationFn: async ({ empresaId, substituir }: { empresaId: number; substituir: boolean }) => {
      return thorjavaMenuApi.importar(empresaId, substituir)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['thorjava-menu'] })
      setDialogImportOpen(false)
      setEmpresaImportId(null)
    },
  })

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleImportar = () => {
    if (!empresaImportId) {
      alert('Selecione uma empresa')
      return
    }

    importarMutation.mutate({
      empresaId: empresaImportId,
      substituir: substituirExistente,
    })
  }

  const contarItens = (items: ThorJavaMenuItemResponse[]): number => {
    let total = items.length
    items.forEach((item) => {
      if (item.children) {
        total += contarItens(item.children)
      }
    })
    return total
  }

  const obterNivelMaximo = (items: ThorJavaMenuItemResponse[]): number => {
    let maxNivel = 0
    items.forEach((item) => {
      if (item.nivel > maxNivel) {
        maxNivel = item.nivel
      }
      if (item.children) {
        const nivelFilhos = obterNivelMaximo(item.children)
        if (nivelFilhos > maxNivel) {
          maxNivel = nivelFilhos
        }
      }
    })
    return maxNivel
  }

  const totalItens = contarItens(menuArvore)
  const nivelMaximo = obterNivelMaximo(menuArvore)

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%' }}>
      <ActionBar>
        {isSuperAdmin && (
          <ImportButton
            onClick={() => setDialogImportOpen(true)}
            title="Importar Menu"
          />
        )}
        <RefreshButton onClick={() => refetch()} disabled={isLoading} title="Atualizar" />
      </ActionBar>

      {!isSuperAdmin && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Acesso Restrito: Super Administrador</strong>
            <br />
            Esta página permite visualizar e gerenciar o menu do sistema ThorJava.
            <br />
            Apenas Super Administradores podem importar e modificar o menu.
          </Typography>
        </Alert>
      )}

      {isSuperAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Menu ThorJava</strong>
            <br />
            Visualize e gerencie a estrutura do menu do sistema ThorJava.
            <br />
            Use "Importar Menu" para sincronizar o menu do banco da empresa padrão.
          </Typography>
        </Alert>
      )}

      {/* Estatísticas */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Total de Itens
            </Typography>
            <Typography variant="h6">{totalItens}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Nível Máximo
            </Typography>
            <Typography variant="h6">{nivelMaximo}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Itens Raiz
            </Typography>
            <Typography variant="h6">{menuArvore.length}</Typography>
          </Box>
        </Box>
      </Paper>

      {/* Árvore do Menu */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Visualização em Árvore" />
          <Tab label="Lista Detalhada" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Erro ao carregar menu: {error instanceof Error ? error.message : 'Erro desconhecido'}
            </Alert>
          )}

          {!isLoading && !error && menuArvore.length === 0 && (
            <Alert severity="info">
              Nenhum item de menu encontrado. Use "Importar Menu" para importar do banco da empresa padrão.
            </Alert>
          )}

          {!isLoading && !error && menuArvore.length > 0 && (
            <ThorJavaMenuTreeView 
              items={menuArvore} 
              defaultExpanded={true}
              onItemClick={() => {
                // Item clicado - pode ser usado para navegação futura
                // logger.debug('Item clicado:', item)
              }}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Erro ao carregar menu: {error instanceof Error ? error.message : 'Erro desconhecido'}
            </Alert>
          )}

          {!isLoading && !error && menuArvore.length === 0 && (
            <Alert severity="info">
              Nenhum item de menu encontrado. Use "Importar Menu" para importar do banco da empresa padrão.
            </Alert>
          )}

          {!isLoading && !error && menuArvore.length > 0 && (
            <Box>
              {menuArvore.map((item, idx) => (
                <Accordion key={item.id} defaultExpanded={idx < 3}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {item.nmmenu}
                      </Typography>
                      <Chip label={`Nível ${item.nivel}`} size="small" />
                      <Chip label={`Seq: ${item.cnseque}`} size="small" variant="outlined" />
                      {item.nmmetodo && (
                        <Chip label={`Método: ${item.nmmetodo}`} size="small" color="primary" />
                      )}
                      {item.infolder === 'S' && (
                        <Chip label="Pasta" size="small" color="secondary" />
                      )}
                      {item.inseparador === 'S' && (
                        <Chip label="Separador" size="small" color="warning" />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">CN Menu:</Typography>
                        <Typography variant="body2">{item.cnmenu}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">CN Menu Pai:</Typography>
                        <Typography variant="body2">{item.cnmenupai || 'Raiz'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Código Ordem:</Typography>
                        <Typography variant="body2">{item.cdordem || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Ícone:</Typography>
                        <Typography variant="body2">{item.nmicon || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Método:</Typography>
                        <Typography variant="body2">{item.nmmetodo || '-'}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Tooltip:</Typography>
                        <Typography variant="body2">{item.nmtooltip || '-'}</Typography>
                      </Box>
                      {item.children && item.children.length > 0 && (
                        <Box sx={{ gridColumn: '1 / -1' }}>
                          <Typography variant="caption" color="text.secondary">
                            Filhos ({item.children.length}):
                          </Typography>
                          <Box sx={{ pl: 2, mt: 1 }}>
                            {item.children.map((child) => (
                              <Typography key={child.id} variant="body2">
                                • {child.nmmenu} (Nível {child.nivel}, Seq: {child.cnseque})
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </TabPanel>
      </Paper>


      {/* Dialog de Importação */}
      <Dialog open={dialogImportOpen} onClose={() => setDialogImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Importar Menu do Banco da Empresa</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Empresa</InputLabel>
              <Select
                value={empresaImportId || ''}
                label="Empresa"
                onChange={(e) => setEmpresaImportId(Number(e.target.value))}
              >
                {empresas
                  .filter((e) => e.banco_dados && e.status === 'A')
                  .map((empresa) => (
                    <MenuItem key={empresa.id} value={empresa.id}>
                      {empresa.nome} ({empresa.banco_dados})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Modo de Importação</InputLabel>
              <Select
                value={substituirExistente ? 'substituir' : 'adicionar'}
                label="Modo de Importação"
                onChange={(e) => setSubstituirExistente(e.target.value === 'substituir')}
              >
                <MenuItem value="adicionar">
                  Adicionar novos (manter existentes)
                </MenuItem>
                <MenuItem value="substituir">
                  Substituir menu existente
                </MenuItem>
              </Select>
            </FormControl>

            {importarMutation.isSuccess && (
              <Alert severity="success">
                {importarMutation.data?.mensagem}
                <br />
                Importados: {importarMutation.data?.total_importados} | 
                Atualizados: {importarMutation.data?.total_atualizados} | 
                Erros: {importarMutation.data?.total_erros}
              </Alert>
            )}

            {importarMutation.isError && (
              <Alert severity="error">
                Erro ao importar: {importarMutation.error instanceof Error ? importarMutation.error.message : 'Erro desconhecido'}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogImportOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleImportar}
            variant="contained"
            disabled={!empresaImportId || importarMutation.isPending}
          >
            {importarMutation.isPending ? <CircularProgress size={20} /> : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default MenuThorJavaPage

